import React, { ReactElement, useCallback, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { useAddToCartAction } from 'b2b-apps/shop/cart/hooks';
import { TOAST_TYPE } from 'b2b-apps/shop/common/components/Toast';
import { showToast } from 'b2b-apps/shop/common/utils/showToast';
import { useCheckPermissions, useLoginAction, useUserData } from 'b2b-apps/shop/context/hooks';
import { PERMISSION } from 'b2b-apps/shop/context/utils/permissions';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const {
      isUserCertified,
      productName,
      sku,
      onClick,
      ...originalProps
    } = props;
    const {
      settings: {
        quantity: stockCount,
        isDisabledByCoreAssortment,
      },
    } = originalProps;

    const {
      isLoggedIn,
      isBlocked,
    } = useUserData();
    const addToCart = useAddToCartAction();
    const hasAccessCartPermissions = useCheckPermissions([PERMISSION.accessCart]);

    const isCxml = useCheckPermissions([PERMISSION.cxml]);
    const canEditCxmlCart = useCheckPermissions([PERMISSION.editCxmlCart]);
    const inCxmlInspectMode = isCxml && !canEditCxmlCart;

    const text = {
      addToCart: t('Add item to cart'),
      notAvailable: t('This item is no longer available for purchase'),
      notPermitted: t('Your account role does not allow purchasing.'),
      inspectionModeSession: t('Cannot modify basket while in inspection mode session.'),
      blockedUser: t('You are not allowed to purchase.'),
      isSoldOut: t('Product is sold out.'),
      notBuyable: t('Product not available for purchase.'),
      maximumQuantityReached: (maxQuantity: number) => t('This is a sell-out item. Available quantity for purchase is {maxQuantity}.', { maxQuantity }),
      maximumOrderQuantityReached: (maxQuantity: number) => t('Maximum quantity allowed per order is {maxQuantity}.', { maxQuantity }),
      redirectReason: t('You have to log in to perform this action'),
      notInCoreAssortment: t('This item cannot be purchased due to restrictions in assortment on your account.'),
    };

    const hasCertificationInfo = useMemo(
      (): boolean => isLoggedIn
        && !isUserCertified
        && hasAccessCartPermissions
        && !isBlocked,
      [
        hasAccessCartPermissions,
        isBlocked,
        isLoggedIn,
        isUserCertified,
      ],
    );

    const redirectToLogin = useLoginAction();

    const handleAddToCart = useCallback(
      (quantity: number): void => {
        if (!isLoggedIn) {
          return redirectToLogin(t('You have to log in to perform this action'));
        }

        if (isDisabledByCoreAssortment) {
          showToast(TOAST_TYPE.error, t('This item cannot be purchased due to restrictions in assortment on your account.'), productName);
          return;
        }

        if (isBlocked) {
          showToast(TOAST_TYPE.error, t('You are not allowed to purchase.'), productName);
          return;
        }

        if (inCxmlInspectMode) {
          showToast(TOAST_TYPE.error, t('Cannot modify basket while in inspection mode session.'), productName);
          return;
        }

        if (!hasAccessCartPermissions) {
          showToast(TOAST_TYPE.error, t('Your account role does not allow purchasing.'), productName);
          return;
        }

        onClick?.(sku, quantity);
        addToCart(sku, quantity, stockCount);
      },
      [
        inCxmlInspectMode,
        addToCart,
        hasAccessCartPermissions,
        isBlocked,
        isLoggedIn,
        productName,
        sku,
        stockCount,
        redirectToLogin,
      ],
    );
    const injectedProps: InjectedProps = {
      hasAccessCartPermissions,
      hasCertificationInfo,
      isBlocked,
      isLoggedIn,
      onAddToCart: handleAddToCart,
      text,
    };

    return <Component {...originalProps} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { withContainer };
